import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Serenity Passage | End-of-Life Doula Columbia, MO</title>
        <meta
          name="description"
          content="Privacy Policy for Serenity Passage end-of-life doula services in Columbia, Missouri. Learn how we protect your personal information and maintain confidentiality."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.serenitypassage.net/privacy-policy" />
      </Helmet>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-rose-100 to-rose-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-rose-600" />
            <h1 className="text-4xl sm:text-5xl font-serif text-gray-800">Privacy Policy</h1>
          </div>
          <p className="text-gray-600">Last updated: January 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-rose max-w-none">
          
          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Introduction</h2>
          <p className="text-gray-700 mb-6">
            Serenity Passage ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website serenitypassage.com or use our end-of-life doula services.
          </p>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Information We Collect</h2>
          <p className="text-gray-700 mb-4">
            We may collect information about you in a variety of ways, including:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
            <li><strong>Personal Data:</strong> Name, email address, phone number, and other contact information you voluntarily provide when contacting us or scheduling a consultation.</li>
            <li><strong>Health Information:</strong> Information related to end-of-life care needs that you choose to share with us during consultations. This information is kept strictly confidential.</li>
            <li><strong>Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, and pages visited.</li>
          </ul>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">How We Use Your Information</h2>
          <p className="text-gray-700 mb-4">
            We use the information we collect to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
            <li>Provide and maintain our end-of-life doula services</li>
            <li>Respond to your inquiries and schedule consultations</li>
            <li>Send you information about our services (with your consent)</li>
            <li>Improve our website and services</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Confidentiality</h2>
          <p className="text-gray-700 mb-6">
            We understand the sensitive nature of end-of-life care. All personal and health-related information shared during consultations is treated with the utmost confidentiality. We do not share your information with third parties except as required by law or with your explicit consent.
          </p>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Data Security</h2>
          <p className="text-gray-700 mb-6">
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
          </p>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Cookies and Tracking</h2>
          <p className="text-gray-700 mb-6">
            Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse cookies, but some features of our website may not function properly.
          </p>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Your Rights</h2>
          <p className="text-gray-700 mb-4">
            You have the right to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Withdraw consent for marketing communications</li>
          </ul>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Contact Us</h2>
          <p className="text-gray-700 mb-6">
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <div className="bg-rose-50 p-6 rounded-lg mb-8">
            <p className="text-gray-800 font-medium">Serenity Passage</p>
            <p className="text-gray-700">Monica Korba, RN</p>
            <p className="text-gray-700">Email: <a href="mailto:mokorba@gmail.com" className="text-rose-600 hover:text-rose-700">mokorba@gmail.com</a></p>
            <p className="text-gray-700">Service Area: Mid-Missouri</p>
          </div>

          <h2 className="text-2xl font-serif text-gray-800 mt-8 mb-4">Changes to This Policy</h2>
          <p className="text-gray-700 mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>

          <div className="mt-12 pt-8 border-t border-rose-200">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
